import React, { useState, useEffect, useMemo } from 'react';
import ReactCountryFlag from 'react-country-flag';
import { Select, SelectOption, SelectGroup } from './Select';
import { currencyService, Currency } from '../../utils/currencyService';

interface CurrencySelectProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  className?: string;
  showCommonFirst?: boolean;
  showFlags?: boolean;
  searchable?: boolean;
}

const CurrencySelect: React.FC<CurrencySelectProps> = ({
  value,
  onChange,
  placeholder = 'Select currency',
  disabled = false,
  error = false,
  className = '',
  showCommonFirst = true,
  showFlags = true,
  searchable = true,
}) => {
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');

  useEffect(() => {
    const loadCurrencies = async () => {
      try {
        setIsLoading(true);
        setErrorMessage('');
        
        const allCurrencies = await currencyService.getAllCurrencies();
        setCurrencies(allCurrencies);
      } catch (error) {
        console.error('Failed to load currencies:', error);
        setErrorMessage('Failed to load currency list');
        setCurrencies([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadCurrencies();
  }, []);

  // Filter currencies based on search query
  const filteredCurrencies = useMemo(() => {
    if (!searchQuery.trim()) {
      return currencies;
    }
    
    return currencies.filter(currency => 
      currency.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
      currency.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      currency.countryName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      currency.symbol.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [currencies, searchQuery]);

  // Prepare select options
  const currencyOptions: SelectOption[] = filteredCurrencies.map(currency => ({
    value: currency.code,
    label: currencyService.formatCurrencyForSelect(currency),
    icon: showFlags ? (
      <ReactCountryFlag
        countryCode={currency.countryCode}
        svg
        style={{
          width: '1.5em',
          height: '1.5em',
          marginRight: '0.5em',
        }}
        title={currency.countryName}
      />
    ) : undefined,
    metadata: {
      symbol: currency.symbol,
      name: currency.name,
      countryName: currency.countryName,
      countryCode: currency.countryCode,
    },
  }));

  // Group options if enabled
  const getOptions = () => {
    if (!showCommonFirst || filteredCurrencies.length <= 20) {
      return currencyOptions;
    }

    const commonCodes = ['USD', 'EUR', 'GBP', 'JPY', 'CAD', 'AUD', 'CHF', 'CNY', 'INR', 'BRL'];
    const commonOptions = currencyOptions.filter(option => commonCodes.includes(option.value));
    const otherOptions = currencyOptions.filter(option => !commonCodes.includes(option.value));

    return [
      {
        label: 'Common Currencies',
        options: commonOptions,
      },
      {
        label: 'All Currencies',
        options: otherOptions,
      },
    ] as SelectGroup[];
  };

  const options = getOptions();

  // Handle search input change
  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
  };

  return (
    <div className="relative">
      <Select
        options={options}
        value={value}
        onChange={onChange}
        placeholder={isLoading ? 'Loading currencies...' : placeholder}
        disabled={disabled || isLoading}
        error={error || !!errorMessage}
        className={className}
        showCustomOptions={true}
        searchable={searchable}
        searchPlaceholder="Search by code, name, or country..."
        onSearchChange={handleSearchChange}
        isLoading={isLoading}
      />

      {isLoading && (
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
        </div>
      )}

      {errorMessage && (
        <p className="mt-1 text-xs text-danger">{errorMessage}</p>
      )}

      {!isLoading && !errorMessage && filteredCurrencies.length > 0 && (
        <p className="mt-1 text-xs text-grey_dark text-end w-full">
          {filteredCurrencies.length} currencies available
          {searchQuery && ` (filtered from ${currencies.length})`}
        </p>
      )}

      {!isLoading && searchQuery && filteredCurrencies.length === 0 && (
        <p className="mt-1 text-xs text-grey_dark">
          No currencies found for "{searchQuery}"
        </p>
      )}
    </div>
  );
};

export default CurrencySelect;